/* Input park: the drawer properties convert with the details-tab round (component-map.md). */
import { FormErrorMessage, Input, InputGroup, InputRightAddon } from '@chakra-ui/react';
import { ClockIcon } from '@heroicons/react/24/outline';
import { useUpdateOrganizationMutation } from '@main/graphql/mutations/UpdateOrganization.admin.generated';
import { useGetOrganizationQuery } from '@main/graphql/queries/GetOrganization.generated';
import { DrawerProperty } from '@main/ui';
import { useState } from 'react';

import { useUpdateOrganizationHandler } from './use-update-org-handler';

const MIN_TIMEOUT_MINUTES = 5;
const MAX_TIMEOUT_MINUTES = 35_791;

export const InactivityTimeoutInput = ({ orgId }: { orgId: string }) => {
  const { data } = useGetOrganizationQuery({ orgId });
  const organization = data?.organizations_by_pk;
  const [updateOrganization, { isLoading }] = useUpdateOrganizationMutation();
  const updateOrgHandler = useUpdateOrganizationHandler(orgId);
  const [error, setError] = useState<string | null>(null);

  if (!organization) {
    return null;
  }

  const savedValue = organization.inactivity_timeout_minutes ?? null;

  const save = (raw: string) => {
    const trimmed = raw.trim();
    const parsed = Number(trimmed);

    if (trimmed === '' || parsed === 0) {
      setError(null);
      persist(null);
      return;
    }

    const minutes = Math.round(parsed);

    if (
      !Number.isFinite(minutes) ||
      minutes < MIN_TIMEOUT_MINUTES ||
      minutes > MAX_TIMEOUT_MINUTES
    ) {
      setError(
        `Enter ${MIN_TIMEOUT_MINUTES}-${MAX_TIMEOUT_MINUTES} minutes, or leave blank or zero to disable.`,
      );
      return;
    }

    setError(null);
    persist(minutes);
  };

  const persist = (nextValue: number | null) => {
    if (nextValue === savedValue) {
      return;
    }

    updateOrgHandler(
      updateOrganization({
        organization_id: orgId,
        updatePayload: { inactivity_timeout_minutes: nextValue },
      }),
    );
  };

  return (
    <DrawerProperty isInvalid={!!error}>
      <DrawerProperty.Label
        htmlFor="inactivity-timeout"
        icon={ClockIcon}
        tooltip="When configured, users are logged out after this many minutes without in-app activity. If a user belongs to multiple organizations, the shortest configured timeout applies."
      >
        Idle timeout
      </DrawerProperty.Label>
      <DrawerProperty.Content px={2}>
        <InputGroup size="sm" maxW="40">
          <Input
            key={savedValue ?? 'off'}
            id="inactivity-timeout"
            type="number"
            min={MIN_TIMEOUT_MINUTES}
            max={MAX_TIMEOUT_MINUTES}
            step={5}
            placeholder="Off"
            isDisabled={isLoading}
            defaultValue={savedValue ?? ''}
            onBlur={(event) => save(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.currentTarget.blur();
              }
            }}
          />
          <InputRightAddon>minutes</InputRightAddon>
        </InputGroup>
        {error && <FormErrorMessage>{error}</FormErrorMessage>}
      </DrawerProperty.Content>
    </DrawerProperty>
  );
};
